import React from "react";
import { EntityProviderType, EntityStorageType } from "./type";
import { LocalStorageKey } from "../../../../shared/utils/enums";
import { useGetRegDetails } from "shared/context/regDetail/dataloader";
import { useGetClientDetails } from "apps/console/modules/client/context/client/dataloader";
import { useGetEventSeries } from "apps/console/components/createUpdateEventSeries/dataloader";
import { useGetProgram } from "apps/console/components/createUpdateProgram/dataloader";
import { useGetEvent } from "apps/console/components/createUpdateEvent/dataloader";
import ProgramFactory from "apps/console/modules/program/workflow/components/programs/programFactory";
import { SessionStorage } from "shared/utils/storage/SessionStorage";
import { useStaticData } from "context/staticData";
import { useGetEnabledFeaturesByRegistrationId } from "apps/console/dataloader";
import { isFeatureEnabled } from "shared/utils";
import PdfTemplateName from "shared/enums/PdfTemplateName";

const initialState: EntityStorageType = {
  clientId: "",
  eventSeriesId: "",
  registrationId: "",
  eventId: "",
  programId: ""
};
const Context = React.createContext<EntityProviderType>({} as EntityProviderType);

export function EntityContextProvider(props: React.PropsWithChildren<{}>) {
  const [state, setState] = React.useState<EntityStorageType>(() =>
    SessionStorage.getData(LocalStorageKey.ENTITY_STORAGE)
  );

  const updateDataInStorage = React.useCallback((data: EntityStorageType) => {
    SessionStorage.setData(LocalStorageKey.ENTITY_STORAGE, data);
  }, []);

  const staticDataObj = useStaticData();
  const { data: clientDetails } = useGetClientDetails(state.clientId);
  const { data: eventSeriesDetails } = useGetEventSeries(state.eventSeriesId);
  const { data: registrationDetails, refetch: refetchRegistrationDetails } = useGetRegDetails({
    registrationId: state.registrationId
  });
  const { data: eventDetails } = useGetEvent({ id: state.eventId });
  const { data: programDetails } = useGetProgram({ programId: state.programId });
  const { data: enabledFeatureMap, refetch: refetchEnabledFeature } = useGetEnabledFeaturesByRegistrationId({
    registrationId: state.registrationId
  });

  const currencyCode = React.useMemo(() => registrationDetails?.currencyCode, [registrationDetails?.currencyCode]);

  const localeId = React.useMemo(() => registrationDetails?.localeId, [registrationDetails?.localeId]);

  const Program = React.useMemo(() => {
    return ProgramFactory(programDetails);
  }, [programDetails]);

  const setClientId = React.useCallback(
    (clientId: string) => {
      setState((data: EntityStorageType) => {
        const updatedData = { ...data, clientId, eventSeriesId: "", registrationId: "" };
        updateDataInStorage(updatedData);
        return updatedData;
      });
    },
    [updateDataInStorage]
  );

  const setEventSeriesId = React.useCallback(
    (eventSeriesId: string) => {
      setState(data => {
        const updatedData = { ...data, eventSeriesId, registrationId: "" };
        updateDataInStorage(updatedData);
        return updatedData;
      });
    },
    [updateDataInStorage]
  );

  const setRegistrationId = React.useCallback(
    (registrationId: string) => {
      setState(data => {
        const updatedData = { ...data, registrationId, eventId: "", programId: "" };
        updateDataInStorage(updatedData);
        return updatedData;
      });
    },
    [updateDataInStorage]
  );

  const setEventId = React.useCallback(
    (eventId: string) => {
      setState(data => {
        const updatedData = { ...data, eventId, programId: "" };
        updateDataInStorage(updatedData);
        return updatedData;
      });
    },
    [updateDataInStorage]
  );

  const setProgramId = React.useCallback(
    (programId: string) => {
      setState(data => {
        const updatedData = { ...data, programId };
        updateDataInStorage(updatedData);
        return updatedData;
      });
    },
    [updateDataInStorage]
  );

  const isPDFTemplateEnabled = React.useCallback(
    (templateName: PdfTemplateName) => isFeatureEnabled({ enabledFeatureMap, templateName }),
    [enabledFeatureMap]
  );

  const registrationTimeZone = React.useMemo(() => {
    if (registrationDetails?.timeZone) {
      return staticDataObj.timezoneDisplayStr?.[registrationDetails.timeZone] ?? "";
    }
    return "";
  }, [registrationDetails?.timeZone, staticDataObj.timezoneDisplayStr]);

  const eventTimeZone = React.useMemo(() => {
    if (eventDetails?.timeZone) {
      return staticDataObj.timezoneDisplayStr?.[eventDetails.timeZone] ?? "";
    }
    return "";
  }, [eventDetails?.timeZone, staticDataObj.timezoneDisplayStr]);

  const programTimeZone = React.useMemo(() => {
    if (programDetails?.timeZone) {
      return staticDataObj.timezoneDisplayStr?.[programDetails.timeZone] ?? "";
    }
    return "";
  }, [programDetails?.timeZone, staticDataObj.timezoneDisplayStr]);

  const flushall = React.useCallback(() => {
    setState(initialState);
    SessionStorage.setData(LocalStorageKey.ENTITY_STORAGE, initialState);
  }, []);

  const isAITextEnhancementEnabled = React.useMemo(() => {
    return Boolean(registrationDetails?.aiFeaturesEnabled && registrationDetails?.aiTextEnhancementEnabled);
  }, [registrationDetails?.aiFeaturesEnabled, registrationDetails?.aiTextEnhancementEnabled]);

  return (
    <Context.Provider
      value={{
        ...state,
        setClientId,
        setEventSeriesId,
        setRegistrationId,
        setEventId,
        setProgramId,
        flushall,
        clientDetails,
        eventSeriesDetails,
        registrationDetails,
        eventDetails,
        programDetails,
        currencyCode,
        localeId,
        Program,
        registrationTimeZone,
        eventTimeZone,
        programTimeZone,
        refetchRegistrationDetails,
        refetchEnabledFeature,
        isPDFTemplateEnabled,
        isAITextEnhancementEnabled
      }}
      {...props}
    />
  );
}

export function useEntity() {
  const context = React.useContext(Context);
  if (context === undefined) {
    throw new Error("useEntity must be used within a EntityContextProvider");
  }
  return context;
}
